import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { buddhismAPI, imageAPI } from '../utils/api';

const REGIONS = ['전체','서울','경기','인천','강원','충북','충남','대전','세종','경북','경남','대구','울산','부산','전북','전남','광주','제주'];

const emptyTemple = {
  name: '', address: '', addressdetail: '', chief_monk: '',
  region: '', sect_id: null, is_preparing: false, commission_rate: 0, manager_phone: '', images: [], item_groups: [],
};

const emptyGroup = { name: '', items: [] };
const emptyItem = { name: '', price: '', description: '', images: [], option_groups: [] };

/* ──────────────────────────────────────── */

const TempleAdmin = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [temple, setTemple]   = useState(emptyTemple);
  const [sects, setSects]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [success, setSuccess] = useState(null);
  const [imgUploading, setImgUploading] = useState(false);

  /* 초기 데이터 로드 */
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const sectRes = await buddhismAPI.getSects();
        setSects(sectRes.data.results || sectRes.data);
        if (isEdit) {
          const res = await buddhismAPI.getById(id);
          const d = res.data;
          setTemple({
            name: d.name || '',
            address: d.address || '',
            addressdetail: d.addressdetail || '',
            chief_monk: d.chief_monk || '',
            region: d.region || '',
            sect_id: d.sect?.id || null,
            is_preparing: d.is_preparing || false,
            commission_rate: d.commission_rate || 0,
            manager_phone: d.manager_phone || '',
            images: d.images || [],
            item_groups: (d.item_groups || []).map(g => ({
              id: g.id,
              name: g.name,
              items: (g.items || []).map(item => ({
                id: item.id,
                name: item.name,
                price: item.price,
                description: item.description,
                images: item.images || [],
                option_groups: item.option_groups || [],
              })),
            })),
          });
        }
      } catch (e) {
        setError('데이터를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id, isEdit]);

  /* 이미지 업로드 */
  const uploadImages = async (files) => {
    setImgUploading(true);
    const uploaded = [];
    
    for (const file of files) {
      const form = new FormData();
      form.append('images', file);
      try {
        const res = await imageAPI.upload(form);
        // 백엔드는 [{"id": "...", "url": "..."}] 형태의 배열을 반환합니다.
        if (Array.isArray(res.data) && res.data.length > 0) {
          uploaded.push({ id: res.data[0].id, url: res.data[0].url });
        } else if (res.data && res.data.id) {
          uploaded.push({ id: res.data.id, url: res.data.url });
        }
      } catch (err) {
        console.error("이미지 업로드 실패:", err);
        alert("이미지 업로드에 실패했습니다.");
      }
    }
    setImgUploading(false);
    return uploaded;
  };

  const handleTempleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const imgs = await uploadImages(files);
    setTemple(p => ({ ...p, images: [...p.images, ...imgs] }));
    e.target.value = '';
  };

  const removeTempleImage = (idx) => {
    setTemple(p => ({ ...p, images: p.images.filter((_, i) => i !== idx) }));
  };

  /* 아이템 그룹 */
  const addGroup = () => setTemple(p => ({ ...p, item_groups: [...p.item_groups, { ...emptyGroup, items: [] }] }));
  const removeGroup = (gi) => setTemple(p => ({ ...p, item_groups: p.item_groups.filter((_, i) => i !== gi) }));
  const updateGroup = (gi, field, val) =>
    setTemple(p => ({ ...p, item_groups: p.item_groups.map((g, i) => i === gi ? { ...g, [field]: val } : g) }));

  /* 아이템 */
  const addItem = (gi) =>
    setTemple(p => ({
      ...p,
      item_groups: p.item_groups.map((g, i) =>
        i === gi ? { ...g, items: [...g.items, { ...emptyItem }] } : g),
    }));
  const removeItem = (gi, ii) =>
    setTemple(p => ({
      ...p,
      item_groups: p.item_groups.map((g, i) =>
        i === gi ? { ...g, items: g.items.filter((_, j) => j !== ii) } : g),
    }));
  const updateItem = (gi, ii, field, val) =>
    setTemple(p => ({
      ...p,
      item_groups: p.item_groups.map((g, i) =>
        i === gi ? {
          ...g,
          items: g.items.map((item, j) => j === ii ? { ...item, [field]: val } : item),
        } : g),
    }));

  const handleItemImageUpload = async (gi, ii, e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const imgs = await uploadImages(files);
    setTemple(p => ({
      ...p,
      item_groups: p.item_groups.map((g, i) =>
        i === gi ? {
          ...g,
          items: g.items.map((item, j) =>
            j === ii ? { ...item, images: [...item.images, ...imgs] } : item),
        } : g),
    }));
    e.target.value = '';
  };

  /* 저장 */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!temple.name.trim()) { setError('사찰명을 입력해주세요.'); return; }
    if (!temple.address.trim()) { setError('주소를 입력해주세요.'); return; }

    setSaving(true);
    setError(null);
    setSuccess(null);

    const payload = {
      name: temple.name.trim(),
      address: temple.address.trim(),
      addressdetail: temple.addressdetail.trim(),
      chief_monk: temple.chief_monk.trim(),
      region: temple.region,
      sect_id: temple.sect_id,
      is_preparing: temple.is_preparing,
      commission_rate: temple.commission_rate,
      manager_phone: temple.manager_phone.trim(),
      images: temple.images.map(img => ({ id: img.id, url: img.url })),
      item_groups: temple.item_groups.map(g => ({
        ...(g.id ? { id: g.id } : {}),
        name: g.name,
        items: g.items.map(item => ({
          ...(item.id ? { id: item.id } : {}),
          name: item.name,
          price: Number(item.price) || 0,
          description: item.description,
          images: item.images.map(img => ({ id: img.id, url: img.url })),
          option_groups: item.option_groups,
        })),
      })),
    };

    try {
      if (isEdit) {
        await buddhismAPI.patch(id, payload);
      } else {
        await buddhismAPI.create(payload);
      }
      setSuccess(isEdit ? '사찰 정보가 수정되었습니다.' : '사찰이 등록되었습니다.');
      setTimeout(() => navigate('/admin/temples'), 1200);
    } catch (err) {
      const d = err.response?.data;
      if (d && typeof d === 'object') {
        const msgs = Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('\n');
        setError(msgs);
      } else {
        setError('저장 중 오류가 발생했습니다.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="ta-loading">
        <span className="ta-dharma-spin">☸</span>
        <p>불러오는 중…</p>
      </div>
    );
  }

  return (
    <div className="ta-wrap">
      {/* 헤더 */}
      <div className="ta-hero">
        <div className="ta-hero-bg">☸</div>
        <Container className="ta-hero-inner">
          <div className="ta-tabs">
            <button className="ta-tab active">사찰 관리</button>
            <button className="ta-tab" onClick={() => navigate('/admin/orders')}>주문 관리</button>
            <button className="ta-tab" onClick={() => navigate('/admin/orders?tab=settlements')}>정산 관리</button>
            <button className="ta-tab" onClick={() => navigate('/admin/users')}>유저 관리</button>
          </div>
          <span className="ta-badge">관리자</span>
          <h1 className="ta-hero-title">{isEdit ? '사찰 수정' : '사찰 등록'}</h1>
          <p className="ta-hero-sub">{isEdit ? '사찰 정보를 수정합니다.' : '새 사찰을 등록합니다.'}</p>
        </Container>
      </div>

      <Container className="ta-container">
        {/* 알림 */}
        {error && <div className="ta-alert ta-alert--error">⚠ {error}</div>}
        {success && <div className="ta-alert ta-alert--success">✓ {success}</div>}

        <form onSubmit={handleSubmit} className="ta-form">

          {/* ── 기본 정보 ── */}
          <div className="ta-section">
            <h2 className="ta-section-title">🏯 기본 정보</h2>
            <div className="ta-grid-2">
              <div className="ta-field">
                <label className="ta-label">사찰명 <span className="ta-req">*</span></label>
                <input className="ta-input" value={temple.name} onChange={e => setTemple(p => ({ ...p, name: e.target.value }))} placeholder="사찰 이름" />
              </div>
              <div className="ta-field">
                <label className="ta-label">지역</label>
                <select className="ta-input" value={temple.region} onChange={e => setTemple(p => ({ ...p, region: e.target.value }))}>
                  <option value="">지역 선택</option>
                  {REGIONS.filter(r => r !== '전체').map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="ta-field">
                <label className="ta-label">주소 <span className="ta-req">*</span></label>
                <input className="ta-input" value={temple.address} onChange={e => setTemple(p => ({ ...p, address: e.target.value }))} placeholder="도로명 주소" />
              </div>
              <div className="ta-field">
                <label className="ta-label">상세 주소</label>
                <input className="ta-input" value={temple.addressdetail} onChange={e => setTemple(p => ({ ...p, addressdetail: e.target.value }))} placeholder="상세 주소" />
              </div>
              <div className="ta-field">
                <label className="ta-label">종파</label>
                <select className="ta-input" value={temple.sect_id || ''} onChange={e => setTemple(p => ({ ...p, sect_id: e.target.value || null }))}>
                  <option value="">종파 선택</option>
                  {sects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="ta-field">
                <label className="ta-label">알림 수신 번호 (문자알림)</label>
                <input className="ta-input" value={temple.manager_phone} onChange={e => setTemple(p => ({ ...p, manager_phone: e.target.value }))} placeholder="01012345678,01087654321 (쉼표로 구분하여 최대 5개)" />
              </div>
              <div className="ta-field ta-field--full">
                <label className="ta-label">사찰 상태</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
                  <input type="checkbox" checked={temple.is_preparing} onChange={e => setTemple(p => ({ ...p, is_preparing: e.target.checked }))} />
                  <strong>준비중</strong> (체크 시 사용자 화면에서 '준비중'으로 표시되며 상품을 담을 수 없습니다)
                </label>
              </div>
              <div className="ta-field">
                <label className="ta-label">정산 수수료율 (%)</label>
                <input className="ta-input" type="number" step="0.1" min="0" max="100" value={temple.commission_rate} onChange={e => setTemple(p => ({ ...p, commission_rate: parseFloat(e.target.value) || 0 }))} placeholder="예: 3.5" />
              </div>
              <div className="ta-field ta-field--full">
                <label className="ta-label">사찰 소개 (주지 스님 정보)</label>
                <textarea className="ta-textarea" rows={4} value={temple.chief_monk} onChange={e => setTemple(p => ({ ...p, chief_monk: e.target.value }))} placeholder="사찰 소개나 주지 스님 정보를 입력하세요" />
              </div>
            </div>
          </div>

          {/* ── 사찰 이미지 ── */}
          <div className="ta-section">
            <h2 className="ta-section-title">🖼 사찰 이미지</h2>
            <label className="ta-upload-btn">
              {imgUploading ? '업로드 중…' : '+ 이미지 추가'}
              <input type="file" accept="image/*" multiple hidden onChange={handleTempleImageUpload} disabled={imgUploading} />
            </label>
            {temple.images.length > 0 && (
              <div className="ta-img-grid">
                {temple.images.map((img, idx) => (
                  <div key={idx} className="ta-img-card">
                    <img src={img.url} alt={`사찰이미지${idx + 1}`} className="ta-img-thumb" />
                    <button type="button" className="ta-img-remove" onClick={() => removeTempleImage(idx)}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── 공양 아이템 그룹 ── */}
          <div className="ta-section ta-section--trad">
            <div className="ta-section-header">
              <h2 className="ta-section-title" style={{ fontFamily: '"Nanum Myeongjo", serif', fontSize: '1.4rem' }}>🪷 공양 아이템</h2>
              <button type="button" className="ta-btn ta-btn--outline-sm" onClick={addGroup}>+ 그룹 추가</button>
            </div>

            {temple.item_groups.length === 0 && (
              <p className="ta-empty-hint">아이템 그룹이 없습니다. 위 버튼으로 추가하세요.</p>
            )}

            {temple.item_groups.map((group, gi) => (
              <div key={gi} className="ta-group-card">
                <div className="ta-group-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '70%' }}>
                    <input
                      className="ta-group-name-input"
                      value={group.name}
                      onChange={e => updateGroup(gi, 'name', e.target.value)}
                      placeholder="그룹명 (예: 공양)"
                    />
                    <span style={{ fontSize: '1.4rem' }}>🪷</span>
                  </div>
                  <button type="button" className="ta-btn--danger-group" onClick={() => removeGroup(gi)}>그룹 삭제</button>
                </div>

                {/* 아이템 목록 */}
                <div className="ta-items-list">
                  {group.items.map((item, ii) => (
                    <div key={ii} className="ta-item-card">
                      <div className="ta-item-header">
                        <span className="ta-item-num">#{ii + 1}</span>
                        <button type="button" className="ta-btn--danger-item" onClick={() => removeItem(gi, ii)}>삭제</button>
                      </div>
                      <div className="ta-grid-3" style={{ rowGap: '20px' }}>
                        <div className="ta-trad-field">
                          <label className="ta-trad-label">아이템명</label>
                          <input className="ta-trad-input" value={item.name} onChange={e => updateItem(gi, ii, 'name', e.target.value)} placeholder="아이템 이름" />
                        </div>
                        <div className="ta-trad-field">
                          <label className="ta-trad-label">가격 (원)</label>
                          <input className="ta-trad-input" type="number" min="0" value={item.price} onChange={e => updateItem(gi, ii, 'price', e.target.value)} placeholder="0" />
                        </div>
                        <div className="ta-trad-field ta-field--full-grid">
                          <label className="ta-trad-label">설명</label>
                          <input className="ta-trad-input" value={item.description} onChange={e => updateItem(gi, ii, 'description', e.target.value)} placeholder="아이템 설명" />
                        </div>
                      </div>

                      {/* 아이템 이미지 */}
                      <div className="ta-item-imgs" style={{ marginTop: '20px' }}>
                        <label className="ta-trad-upload">
                          + 이미지
                          <input type="file" accept="image/*" multiple hidden onChange={e => handleItemImageUpload(gi, ii, e)} disabled={imgUploading} />
                        </label>
                        {item.images.map((img, iidx) => (
                          <div key={iidx} className="ta-img-card ta-img-card--sm">
                            <img src={img.url} alt="" className="ta-img-thumb" />
                            <button type="button" className="ta-img-remove"
                              onClick={() => updateItem(gi, ii, 'images', item.images.filter((_, x) => x !== iidx))}>✕</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <button type="button" className="ta-trad-add-btn" onClick={() => addItem(gi)}>+ 아이템 추가</button>
                </div>
              </div>
            ))}
          </div>

          {/* ── 액션 버튼 ── */}
          <div className="ta-actions-trad">
            <button type="button" className="ta-btn-cancel" onClick={() => navigate(-1)} disabled={saving}>← 취소</button>
            <button type="submit" className="ta-btn-save" disabled={saving || imgUploading}>
              {saving ? '저장 중…' : isEdit ? '✓ 수정 완료' : '✓ 사찰 등록'}
            </button>
          </div>
        </form>
      </Container>

      <style>{`
        :root {
          --ta-teal: #009590;
          --ta-teal-dark: #007873;
          --ta-pale: #e8f5f5;
          --ta-text: #2c3e35;
          --ta-muted: #6e8a84;
          --ta-border: #e4edec;
          --ta-red: #e74c3c;
        }
        .ta-loading { text-align:center; padding:100px 20px; color:var(--ta-muted); }
        .ta-dharma-spin { font-size:2.5rem; display:block; animation:taSpin 4s linear infinite; color:var(--ta-teal); opacity:.4; }
        @keyframes taSpin { to { transform:rotate(360deg); } }

        .ta-wrap { min-height:100vh; background:#f8fbfb; }

        .ta-hero {
          position:relative; background:linear-gradient(150deg,#f0fafa,#d5efee);
          padding:52px 0 40px; text-align:center; overflow:hidden;
        }
        .ta-hero-bg {
          position:absolute; font-size:16rem; color:var(--ta-teal); opacity:.04;
          top:50%; left:50%; transform:translate(-50%,-50%); pointer-events:none;
        }
        .ta-hero-inner { position:relative; z-index:2; }
        .ta-badge {
          display:inline-block; font-size:.7rem; font-weight:700; letter-spacing:.18em;
          color:var(--ta-teal); border:1px solid var(--ta-teal); border-radius:2px;
          padding:3px 12px; margin-bottom:10px; text-transform:uppercase;
        }
        .ta-hero-title { font-size:2rem; font-weight:700; color:var(--ta-teal); margin:6px 0 8px; }
        .ta-hero-sub { font-size:.9rem; color:var(--ta-muted); margin:0; }

        .ta-container { padding-top:32px; padding-bottom:80px; max-width:900px; }

        .ta-alert {
          padding:14px 18px; border-radius:4px; margin-bottom:20px;
          font-size:.88rem; border-left:3px solid;
        }
        .ta-alert--error { background:#fff8f8; border-color:var(--ta-red); color:#c0392b; white-space:pre-wrap; }
        .ta-alert--success { background:#f0fafa; border-color:var(--ta-teal); color:var(--ta-teal-dark); }

        .ta-form { display:flex; flex-direction:column; gap:24px; }

        .ta-section {
          background:#fff; border:1px solid var(--ta-border); border-radius:8px;
          padding:28px;
        }
        .ta-section-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:20px; }
        .ta-section-title { font-size:1rem; font-weight:700; color:var(--ta-text); margin:0 0 20px; }
        .ta-section-header .ta-section-title { margin:0; }

        .ta-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
        .ta-grid-3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:12px; }
        .ta-field { display:flex; flex-direction:column; gap:5px; }
        .ta-field--full { grid-column:1/-1; }
        .ta-field--full-grid { grid-column:1/-1; }

        .ta-label { font-size:.85rem; font-weight:600; color:var(--ta-text); }
        .ta-label-sm { font-size:.78rem; font-weight:600; color:var(--ta-muted); }
        .ta-req { color:var(--ta-red); }

        .ta-input, .ta-textarea {
          border:1px solid var(--ta-border); border-radius:4px; padding:10px 13px;
          font-size:.9rem; color:var(--ta-text); outline:none;
          font-family:'Noto Sans KR',sans-serif; transition:border-color .2s,box-shadow .2s;
          background:#fff; width:100%;
        }
        .ta-input:focus, .ta-textarea:focus {
          border-color:var(--ta-teal); box-shadow:0 0 0 3px rgba(0,149,144,.08);
        }
        .ta-textarea { resize:vertical; }
        .ta-input-sm {
          border:1px solid var(--ta-border); border-radius:4px; padding:8px 10px;
          font-size:.85rem; color:var(--ta-text); outline:none; width:100%;
          font-family:'Noto Sans KR',sans-serif; transition:border-color .2s;
          background:#fff;
        }
        .ta-input-sm:focus { border-color:var(--ta-teal); }

        .ta-upload-btn {
          display:inline-flex; align-items:center; gap:6px;
          padding:9px 18px; border-radius:4px; font-size:.85rem; font-weight:600;
          background:var(--ta-pale); color:var(--ta-teal); cursor:pointer;
          border:1px dashed var(--ta-teal); transition:background .2s; margin-bottom:14px;
        }
        .ta-upload-btn:hover { background:#d0eeee; }
        .ta-upload-btn--sm { padding:5px 12px; font-size:.78rem; margin-bottom:0; }

        .ta-img-grid { display:flex; flex-wrap:wrap; gap:12px; }
        .ta-img-card { position:relative; width:90px; height:90px; border-radius:6px; overflow:hidden; border:1px solid var(--ta-border); }
        .ta-img-card--sm { width:68px; height:68px; }
        .ta-img-thumb { width:100%; height:100%; object-fit:cover; }
        .ta-img-remove {
          position:absolute; top:3px; right:3px; width:20px; height:20px;
          border-radius:50%; background:rgba(0,0,0,.55); border:none; color:#fff;
          font-size:.65rem; cursor:pointer; display:flex; align-items:center; justify-content:center;
        }

        .ta-group-card {
          border:1px solid var(--ta-border); border-radius:6px; padding:18px;
          margin-bottom:16px; background:#fafefe;
        }
        .ta-group-header { display:flex; gap:10px; align-items:center; margin-bottom:14px; }
        .ta-group-name-input { flex:1; }

        .ta-items-list { display:flex; flex-direction:column; gap:12px; }
        .ta-item-card {
          background:#fff; border:1px solid var(--ta-border); border-radius:4px;
          padding:14px;
        }
        .ta-item-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:10px; }
        .ta-item-num { font-size:.78rem; font-weight:700; color:var(--ta-muted); }
        .ta-item-imgs { display:flex; flex-wrap:wrap; align-items:center; gap:8px; margin-top:10px; }

        .ta-add-item-btn {
          padding:9px; border:1px dashed var(--ta-border); border-radius:4px;
          background:none; color:var(--ta-muted); font-size:.85rem; cursor:pointer;
          transition:all .2s; width:100%;
        }
        .ta-add-item-btn:hover { border-color:var(--ta-teal); color:var(--ta-teal); background:var(--ta-pale); }

        .ta-empty-hint { color:var(--ta-muted); font-size:.88rem; text-align:center; padding:24px; }

        /* 버튼 */
        .ta-btn {
          display:inline-flex; align-items:center; gap:6px; padding:11px 24px;
          border-radius:4px; font-size:.9rem; font-weight:600; cursor:pointer;
          border:none; font-family:'Noto Sans KR',sans-serif; transition:all .2s;
        }
        .ta-btn--primary { background:var(--ta-teal); color:#fff; }
        .ta-btn--primary:hover:not(:disabled) { background:var(--ta-teal-dark); }
        .ta-btn--ghost { background:#f3f4f6; color:#555; }
        .ta-btn--ghost:hover:not(:disabled) { background:#e5e7eb; }
        .ta-btn--outline-sm {
          padding:7px 14px; font-size:.82rem; background:none;
          border:1px solid var(--ta-teal); color:var(--ta-teal); cursor:pointer;
          border-radius:4px; font-weight:600; transition:all .2s;
        }
        .ta-btn--outline-sm:hover { background:var(--ta-pale); }
        .ta-btn--danger-sm {
          padding:7px 14px; font-size:.82rem; background:none;
          border:1px solid var(--ta-red); color:var(--ta-red); cursor:pointer;
          border-radius:4px; font-weight:600; transition:all .2s; white-space:nowrap;
        }
        .ta-btn--danger-sm:hover { background:#fff8f8; }
        .ta-btn--danger-xs {
          padding:4px 10px; font-size:.75rem; background:none;
          border:1px solid var(--ta-red); color:var(--ta-red); cursor:pointer;
          border-radius:3px; font-weight:600;
        }
        .ta-btn:disabled { opacity:.5; cursor:not-allowed; }

        .ta-actions {
          display:flex; justify-content:space-between; align-items:center; gap:12px;
          padding-top:8px; border-top:1px solid var(--ta-border);
        }

        @media(max-width:640px) {
          .ta-grid-2 { grid-template-columns:1fr; }
          .ta-grid-3 { grid-template-columns:1fr 1fr; }
          .ta-section { padding:18px; }
          .ta-actions { flex-direction:column; }
          .ta-btn { width:100%; justify-content:center; }
        }
        /* ── 전통 테마 공양 섹션 CSS ── */
        .ta-section--trad {
          background: transparent;
          border: none;
          padding: 0;
          margin-top: 40px;
        }
        
        .ta-group-card {
          background-color: #FAF6ED;
          border-radius: 8px;
          padding: 24px;
          margin-bottom: 30px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          position: relative;
          overflow: hidden;
        }
        
        .ta-group-card::before {
          content: "";
          position: absolute;
          top: 8px; left: 8px; right: 8px; bottom: 8px;
          border: 1px solid #E8DCC2;
          pointer-events: none;
        }

        .ta-group-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          position: relative;
          z-index: 2;
          border-bottom: 1px solid #E8DCC2;
          padding-bottom: 12px;
        }

        .ta-group-name-input {
          font-family: "Nanum Myeongjo", "Batang", serif;
          font-size: 1.8rem;
          font-weight: 700;
          color: #3E3025;
          background: transparent;
          border: none !important;
          box-shadow: none !important;
          padding: 0;
          width: 100%;
        }
        .ta-group-name-input:focus { outline: none; }

        .ta-btn--danger-group {
          background: linear-gradient(145deg, #901C24, #751D1D);
          color: #fff;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          font-size: 0.9rem;
          cursor: pointer;
          font-weight: 600;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .ta-items-list {
          display: flex;
          flex-direction: column;
          gap: 20px;
          position: relative;
          z-index: 2;
        }

        .ta-item-card {
          background: transparent;
          border: 3px double #E0D3B8;
          padding: 20px;
          border-radius: 4px;
          position: relative;
          overflow: hidden;
        }

        .ta-item-card::after {
          content: "";
          position: absolute;
          bottom: -30px; right: -30px;
          width: 180px; height: 180px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath fill='none' stroke='%23D4C4A3' stroke-width='1.5' d='M50,85 C20,85 10,60 10,50 C10,40 30,30 50,15 C70,30 90,40 90,50 C90,60 80,85 50,85 Z' /%3E%3Cpath fill='none' stroke='%23D4C4A3' stroke-width='1.5' d='M50,85 C35,85 25,65 25,50 C25,40 35,35 50,20 C65,35 75,40 75,50 C75,65 65,85 50,85 Z' /%3E%3Cpath fill='none' stroke='%23D4C4A3' stroke-width='1.5' d='M50,85 C45,85 40,70 40,55 C40,45 45,40 50,30 C55,40 60,45 60,55 C60,70 55,85 50,85 Z' /%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-size: contain;
          pointer-events: none;
          opacity: 0.5;
        }

        .ta-item-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          position: relative;
          z-index: 2;
        }

        .ta-item-num {
          background: #C4A265;
          color: #fff;
          font-weight: bold;
          font-size: 0.85rem;
          width: 32px; height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
        }

        .ta-btn--danger-item {
          background: linear-gradient(145deg, #901C24, #751D1D);
          color: #fff;
          border: none;
          padding: 4px 12px;
          border-radius: 4px;
          font-size: 0.8rem;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .ta-trad-field {
          display: flex;
          flex-direction: column;
          gap: 4px;
          position: relative;
          z-index: 2;
        }

        .ta-trad-label {
          font-size: 0.85rem;
          font-weight: 600;
          color: #4A4036;
        }

        .ta-trad-input {
          border: none;
          border-bottom: 1px solid #D4C4A3;
          background: transparent;
          padding: 6px 0;
          font-size: 0.95rem;
          color: #333;
          outline: none;
          transition: border-color 0.2s;
        }
        .ta-trad-input:focus { border-bottom: 2px solid #009590; }

        .ta-trad-upload {
          border: 1px solid #82B5A5;
          color: #82B5A5;
          background: transparent;
          padding: 6px 14px;
          border-radius: 4px;
          font-size: 0.85rem;
          cursor: pointer;
          display: inline-block;
          font-weight: 600;
          transition: all 0.2s;
          position: relative;
          z-index: 2;
        }
        .ta-trad-upload:hover { background: rgba(130,181,165,0.1); }

        .ta-trad-add-btn {
          width: 100%;
          background: #9FCAC0;
          color: #2F5D53;
          border: none;
          padding: 14px;
          border-radius: 4px;
          font-size: 0.95rem;
          font-weight: 700;
          cursor: pointer;
          margin-top: 10px;
          position: relative;
          z-index: 2;
          transition: background 0.2s;
        }
        .ta-trad-add-btn:hover { background: #8DBEB3; }

        .ta-actions-trad {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 40px;
          padding-top: 20px;
        }

        .ta-btn-cancel {
          background: #E4DED3;
          color: #5A5145;
          border: none;
          padding: 12px 24px;
          border-radius: 4px;
          font-weight: 600;
          cursor: pointer;
        }
        .ta-btn-save {
          background: #145952;
          color: #fff;
          border: none;
          padding: 12px 32px;
          border-radius: 30px;
          font-weight: 700;
          font-size: 1rem;
          cursor: pointer;
          box-shadow: 0 4px 10px rgba(20,89,82,0.3);
        }
      `}</style>
    </div>
  );
};

export default TempleAdmin;
